import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '../../../services/toast.service';
import { ThirdPartiesService } from '../../../services/third-parties.service';
import { LoadingService } from '../../../services/loading.service';
import { DocumentTypeEnum } from '../../../enums/DocumentTypeEnum';

@Component({
  selector: 'app-new-third-partie',
  templateUrl: './new-third-partie.component.html',
  styleUrl: './new-third-partie.component.scss'
})
export class NewThirdPartieComponent implements OnInit {

  thirdPartyForm!: FormGroup;
  thirdPartyId: any = null;
  documentTypes = Object.entries(DocumentTypeEnum).map(([key, value]) => ({ key, value }));

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private _thirdPartyService: ThirdPartiesService,
    private toastService: ToastService,
    private _loadingService : LoadingService
  ) { }

  ngOnInit(): void {
    this.thirdPartyForm = this.fb.group({
      commercialName: [''],
      firstName: [''],
      lastName: [''],
      documentId: ['', Validators.required],
      typeDocument: ['',Validators.required],
      expeditionPlace: [''],
      phone: [''],
      cellPhone: ['',Validators.required],
      email: ['', [Validators.email, Validators.required]],
      addressLine1: [''],
      addressLine2: [''],
      city: [''],
      country: [''],
      observations: [''],
      bigRetainers: [false]
    }, { validators: this.nameValidator });

    this.route.paramMap.subscribe(params => {
      const id: number = Number(params.get('id'));
      this.thirdPartyId = id;
      if (id) {
        this.getThirdPartyById(id);
      }
    });
  }

  /**
   * Validador personalizado: debe tener commercialName O (firstName Y lastName)
   */
  private nameValidator(control: AbstractControl): ValidationErrors | null {
    const commercialName = control.get('commercialName')?.value?.trim();
    const firstName = control.get('firstName')?.value?.trim();
    const lastName = control.get('lastName')?.value?.trim();

    const hasCommercialName = commercialName && commercialName.length > 0;
    const hasPersonalName = firstName && firstName.length > 0 && lastName && lastName.length > 0;

    if (!hasCommercialName && !hasPersonalName) {
      return { nameRequired: true };
    }
    return null;
  }

  getThirdPartyById(id: number) {
    this._loadingService.show();
    this._thirdPartyService.getById(id).subscribe({
      next: (response) => {
        this.thirdPartyForm.patchValue({
          commercialName: response.data.commercialName,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          documentId: response.data.documentId,
          typeDocument: response.data.typeDocument,
          expeditionPlace: response.data.expeditionPlace,
          phone: response.data.phone,
          cellPhone: response.data.cellPhone,
          email: response.data.email,
          addressLine1: response.data.addressLine1,
          addressLine2: response.data.addressLine2,
          city: response.data.city,
          country: response.data.country,
          observations: response.data.observations,
          bigRetainers: response.data.bigRetainers
        });
        this._loadingService.hide();
      },
      error: () => {
        this._loadingService.hide();
        this.toastService.showToast('error', 'Se produjo un error al obtener los datos.');
      }
    });
  }

  onSubmit() {
    this._loadingService.show();
    const newThirdParty = {
      ...this.thirdPartyForm.value,
      bigRetainers: Boolean(this.thirdPartyForm.value.bigRetainers)
    };

    if (this.thirdPartyForm.valid) {
      if (this.thirdPartyId) {
        // Actualizar tercero existente
        this._thirdPartyService.update(this.thirdPartyId, newThirdParty).subscribe({
          next: () => {
            this.handleSuccess('Tercero actualizado correctamente.');
          },
          error: (error) => {
            const errorMessage = error?.error?.errorMessage || error?.error?.message || 'Se produjo un error al actualizar el tercero.';
            this.handleError(errorMessage);
          }
        });
      } else {
        // Crear nuevo tercero
        this._thirdPartyService.insert(newThirdParty).subscribe({
          next: () => {
            this.handleSuccess('Tercero creado correctamente.');
          },
          error: (error) => {
            const errorMessage = error?.error?.errorMessage || error?.error?.message || 'Se produjo un error al crear el tercero.';
            this.handleError(errorMessage);
          }
        });
      }
    }
  }

  private handleSuccess(message: string): void {
    this._loadingService.hide();
    this.toastService.showToast('success', message);
    this.router.navigate(['/third-parties']);
  }

  private handleError(message: string): void {
    this._loadingService.hide();
    this.toastService.showToast('error', message);
  }

  goBack(): void {
    this.router.navigate(['/third-parties']);
  }

}
